// messagesApi.ts — the ONE place every message-thread API-shape assumption
// lives. Mirrors Procurement/poApi.ts: types + fetch functions, nothing else.
//
// Backend: api/src/messages/messages-router.js + messages-service.js (ERP UX
// mini-chunk, "chatter"). ONE table (migration 013) anchored polymorphically
// at (entity_type, entity_id) — any document type opts in by adding one entry
// to the ENTITY_TYPES allowlist server-side; this module never needs to know
// which types exist, it just calls the generic route.
//
// GET  /api/messages/:entityType/:entityId  → { items }, oldest first
// POST /api/messages/:entityType/:entityId  → the created item, 201
//
// kind: 'system' rows are read-only lifecycle entries merged in server-side
// (e.g. a purchase order's purchase_order_events) — there is no write path
// for them here, this module only ever posts 'comment' rows.
//
// @-mentions (migration 015, message_mentions): mention_user_ids travels
// alongside body on create. The backend resolves Slack DMs from it; this
// module just passes the ids through.

import { useApi } from '../../apiHelpers';

// ---------------------------------------------------------------------------
// Row shape
// ---------------------------------------------------------------------------

export type MessageKind = 'comment' | 'system';

/** One entry in a document's thread. `id` is prefixed per source
 * ('msg-<n>' for a human comment, 'evt-<uuid>' for a merged-in lifecycle
 * event) — both are real React keys and must not be stripped or re-derived.
 * `channel` is present only when it is not 'app' (the unremarkable case);
 * when present it is one of 'slack' | 'import' | 'system'. `user_name` is
 * null for a hard-deleted user OR a system action nobody performed — render
 * "System" for a null name on a `kind: 'system'` row, "Former user" for a
 * null name on a `kind: 'comment'` row (never the same fallback for both). */
export interface MessageItem {
  id: string;
  kind: MessageKind;
  body: string;
  user_name: string | null;
  /** The author's chosen face (users columns, migration 158); null is the default
   * face, and a null-name row carries three nulls. docs/features/user-avatars.md. */
  avatar_color: string | null;
  avatar_shape: string | null;
  avatar_accessory: string | null;
  channel?: string;
  created_at: string; // ISO timestamp
  /** Present only on a comment merged in from a RELATED document — a tour thread
   * carries its shows' comments and labels each with the show name. Read-only
   * here: replying still posts to the document you are looking at. */
  source_label?: string;
}

export interface MessageListResponse {
  items: MessageItem[];
}

/** POST body. mention_user_ids is omitted entirely when there are none,
 * matching the house convention of not sending an empty-array "no-op" key
 * (see poApi.ts close()'s reason handling for the same idiom). */
export interface PostMessageInput {
  body: string;
  mention_user_ids?: string[];
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Every failing response on this router is { error: { code, message } }.
 * apiHelpers attaches the parsed body to err.data and the message to
 * err.message, so callers do: err?.data?.error?.message || err?.message.
 * Known codes: empty_body, body_too_long (4000 char cap), unknown_mention
 * (all 400); unknown_entity_type, entity_not_found (404). */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    [key: string]: unknown;
  };
}

// ---------------------------------------------------------------------------
// Mention eligibility
// ---------------------------------------------------------------------------

/** GET /api/users/active row shape — id/firstname/lastname plus the
 * capability booleans needed to compute mention eligibility (below). Not
 * every consumer of that endpoint needs these three fields (POA mentions,
 * gold stars, receipt review, card manager, event form all still just read
 * id/firstname/lastname), but they are cheap booleans and additive to the
 * response, so one endpoint serves everyone. */
export interface ActiveUser {
  id: string;
  firstname: string;
  lastname: string | null;
  isadmin: boolean;
  can_admin: boolean;
  can_procurement: boolean;
  // Added for the airspace thread's gate. Every flag a MENTION_ELIGIBILITY
  // predicate reads MUST be selected by GET /users/active — a flag that is not in
  // the payload is `undefined`, which is falsy, which silently narrows the picker
  // instead of widening it. That is the opposite of the failure the map's doc
  // comment describes, and it is the quiet one.
  can_pilot: boolean;
  can_operations: boolean;
  can_producer: boolean;
  can_animator: boolean;
}

/**
 * Per-entity-type mention-notification eligibility, mirroring that entity's
 * capability gate in api/src/messages/messages-service.js ENTITY_TYPES (one
 * layer down: api/src/middleware/require-capability.js's
 * `isadmin || can_admin || capabilities.some(cap => user[can_${cap}])`).
 *
 * A user who fails this predicate can still be @mentioned — the mention row
 * is harmless — but the backend (messages-notify.js) will never DM them: the
 * DM carries an excerpt of the thread body, and notifying someone who cannot
 * pass the document's own read gate would leak thread content around that
 * gate. Filtering the composer's picker to eligible users is a UX courtesy so
 * people don't @ a colleague who silently hears nothing — it is NOT the
 * security boundary, the backend enforces that regardless of what this map
 * says.
 *
 * ADDING AN ENTITY TYPE: add its predicate here alongside adding it to the
 * backend allowlist. A type with no entry here is simply not filtered (the
 * picker shows every active user) — an omission forfeits the courtesy, it
 * never widens who actually gets notified.
 */
export const MENTION_ELIGIBILITY: Record<string, (user: ActiveUser) => boolean> = {
  // requireCapability('procurement', 'admin') — see messages-service.js
  // ENTITY_TYPES.purchase_order.capabilities.
  purchase_order: (user) => !!(user.isadmin || user.can_admin || user.can_procurement),

  // ENTITY_TYPES.event.capabilities is 'authenticated' — any signed-in user can
  // read an event, so every active user is mention-eligible. Listed explicitly
  // rather than omitted: an omission also shows everyone, but only by accident,
  // and this way the map still reads as a mirror of the backend allowlist.
  event: () => true,

  // ENTITY_TYPES.tour.capabilities is 'authenticated' too.
  tour: () => true,

  // Mirrors ENTITY_TYPES.event_airspace.notifyCapabilities (['pilot']), NOT its
  // wider read gate. operations and producer may READ the airspace thread but
  // /pilots/airspace bounces them, so the DM's link would go nowhere — the backend
  // will not notify them and the picker should not imply otherwise. See the comment
  // on notifyCapabilities in messages-service.js for why that gap exists.
  event_airspace: (user) => !!(user.isadmin || user.can_admin || user.can_pilot),

  // ENTITY_TYPES.creative_production.capabilities: Spark's own gate.
  creative_production: (user) => !!(user.isadmin || user.can_admin || user.can_animator || user.can_producer || user.can_pilot),
};

// ---------------------------------------------------------------------------
// The API
// ---------------------------------------------------------------------------

export function useMessagesApi() {
  const api = useApi();

  return {
    list: (entityType: string, entityId: string | number): Promise<MessageListResponse> =>
      api.get(`/messages/${entityType}/${entityId}`),

    create: (
      entityType: string,
      entityId: string | number,
      payload: PostMessageInput
    ): Promise<MessageItem> =>
      api.post(`/messages/${entityType}/${entityId}`, { body: payload }),
  };
}
